import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { ReactComponent as SvgSearchClose } from '@img/icons/close.svg';
import { ReactComponent as SvgSearch } from '@img/icons/search.svg';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.div `
  ${setCssVar('accentColor', brand.hta.green)};
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('fgColor', brand.black)};
  ${setCssVar('fgColorAlt', brand.grey)};

  .theme-apl & {
    ${setCssVar('accentColor', brand.apl.green)};
    ${setCssVar('bgColor', brand.white)};
    ${setCssVar('fgColor', brand.black)};
    ${setCssVar('fgColorAlt', brand.grey)};
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.green)};
    ${setCssVar('bgColor', brand.grey)};
    ${setCssVar('fgColor', brand.white)};
    ${setCssVar('fgColorAlt', brand.white)};
  }

  max-width: 400px;

  @media ${until(Device.TabletLarge)} {
    display: none;
    background: ${cssVar('bgColor')};
    height: 66px;
    padding: 8px 10px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;

    ${({ active }) => active &&
    css `
        display: block;
      `}
  }

  @media ${from(Device.TabletLarge)} {
    position: relative;
    padding-top: 0;
    margin: 20px 0;
  }
`;
const Inner = styled.div `
  position: relative;
  display: flex;

  @media ${from(Device.TabletLarge)} {
    display: block;
  }
`;
const Input = styled.input `
  display: block;
  flex: 1 1 auto;
  width: 100%;
  background: ${cssVar('bgColor')};
  border: 1px solid ${cssVar('accentColor')};
  border-radius: 1000000px;
  color: ${cssVar('fgColorAlt')};
  font-size: 16px;
  line-height: 30px;
  outline: none;
  padding: 9px 14px 9px 50px;
  transition: border-color 0.15s ease-out;

  @media ${from(Device.TabletLarge)} {
    min-width: 300px;
    padding: 10px 14px 10px 50px;
  }

  @media ${from(Device.Desktop)} {
    min-width: 400px;
  }

  &:focus {
    outline: none;
  }

  &.focus-visible {
    border-color: ${cssVar('fgColor')};
  }
`;
const Submit = styled.button `
  position: absolute;
  left: 1px;
  top: 1px;
  display: flex;
  align-content: center;
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${cssVar('fgColorAlt')};
  flex: 0 0 45px;
  height: 50px;
  padding: 10px;
  justify-content: center;
  justify-items: center;
  width: 50px;
  border-radius: 50%;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    color: ${cssVar('accentColor')};
  }
`;
const SubmitLabel = styled.span `
  ${srOnly}
`;
const SubmitIcon = styled(SvgSearch) `
  display: block;
  color: currentColor;
  height: 26px;
  width: 26px;
  transition: color 0.3s;

  @media ${from(Device.TabletLarge)} {
    height: 18px;
    width: 18px;
  }
`;
const Close = styled.button `
  display: flex;
  align-content: center;
  align-items: center;
  background: none;
  border: none;
  color: ${cssVar('fgColor')};
  flex: 0 0 45px;
  height: 50px;
  margin: 0 0 0 16px;
  padding: 0;
  justify-content: center;
  justify-items: center;
  width: 45px;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    background: none;
    color: ${cssVar('fgColor')};
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const CloseIcon = styled(SvgSearchClose) `
  display: block;
  color: currentColor;
  height: 23px;
  width: 23px;
`;
const CloseLabel = styled.span `
  ${srOnly}
`;
const ResultsContainer = styled(m.div) `
  position: absolute;
  top: 100%;
  left: 0;
  background: ${cssVar('bgColor')};
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.2);
  max-height: 410px;
  width: 100%;
  z-index: 3;

  @media ${until(Device.TabletLarge)} {
    height: calc(100vh - 65px);
    max-height: none;
  }
`;
const ResultItem = styled.a `
  ${fonts.sourceSansPro.light}
  position: relative;
  display: flex;
  align-items: center;
  color: ${cssVar('fgColor')};
  height: 45px;
  font-size: 18px;
  line-height: 23px;
  overflow: hidden;
  padding: 0 10px 0 50px;
  text-decoration: none;
  text-indent: 4px;
  white-space: nowrap;

  &:focus,
  &:hover {
    color: ${cssVar('fgColor')};
    text-decoration: underline;

    ${() => ResultItemText} {
      transform: translate3d(10px, 0, 0.1px);
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    display: block;
    border-bottom: 1px solid ${brand.greyVeryLight};
    margin: 0 10px;
    width: 100%;
  }

  &:last-child::after {
    content: none;
  }
`;
const ResultItemText = styled.span `
  overflow: hidden;
  text-overflow: ellipsis;
  transform: translate3d(0, 0, 0.1px);
  transition: transform ease-out 0.15s;
`;
export default {
    Close,
    CloseIcon,
    CloseLabel,
    Container,
    Inner,
    Input,
    ResultItem,
    ResultItemText,
    ResultsContainer,
    Submit,
    SubmitIcon,
    SubmitLabel,
};
